import { z } from "zod";
// ── Slot production & miss attribution ──
// The status vocabulary is Aztec's own — the six-case proposer taxonomy from
// ValidatorStatusInSlot in @aztec-labs/stdlib/validators, plus 'unknown' for slots that fell
// outside the sentinel's history window. Restated here rather than derived from the
// drizzle pgEnum so the API contract stays independent of the storage layer (same
// convention as health.ts).
//
// We do not define a parallel set of friendlier names: the node is the authority on what
// these mean, a second vocabulary would need syncing with theirs forever, and anyone
// cross-checking against node_getValidatorsStats would have to translate. Where a name
// invites the wrong conclusion, the endpoint's OpenAPI description carries an explainer
// table — docs rather than payload, since the taxonomy is fixed and a consumer reads it once.
export const slotStatusSchema = z.enum([
    "checkpoint-mined",
    "checkpoint-valid",
    "checkpoint-invalid",
    "checkpoint-unvalidated",
    "checkpoint-missed",
    "blocks-missed",
    "unknown",
]);
export const productionStatsQuerySchema = z.object({
    // ── Selection: exactly one of from/to · epoch · slots · hours ──
    from: z
        .string()
        .optional()
        .describe("First slot of the range (inclusive). Pair with `to`.")
        .meta({ example: "28169" }),
    to: z
        .string()
        .optional()
        .describe("Last slot of the range (inclusive). Pair with `from`.")
        .meta({ example: "32474" }),
    epoch: z
        .string()
        .optional()
        .describe("Select a whole epoch — expands to its slot range using the network's epoch duration.")
        .meta({ example: "1010" }),
    slots: z
        .string()
        .optional()
        .describe("Comma-separated explicit slot numbers (max 1000). Use to attribute a known list of " +
        "missed slots in one call.")
        .meta({ example: "32341,32342,32348,32349" }),
    hours: z
        .string()
        .optional()
        .describe("Trailing wall-clock window in hours (max 720), converted to a slot range using the " +
        "network's slot duration. Not subject to the `from`/`to` slot cap — it bounds itself, so " +
        "the full 720 hours is always requestable however many slots that spans.")
        .meta({ example: "24" }),
    // ── Filters: narrow the returned `slots` rows only. Aggregates always describe
    // the whole selection, so a filter never removes the mined baseline. ──
    status: z
        .string()
        .optional()
        .describe("Comma-separated status filter, applied to the `slots` rows only.")
        .meta({ example: "blocks-missed,checkpoint-valid" }),
    missedOnly: z
        .enum(["true", "false"])
        .optional()
        .describe("When true, `slots` omits checkpoint-mined rows.")
        .meta({ example: "true" }),
    // ── Shape ──
    include: z
        .string()
        .optional()
        .describe("Comma-separated optional sections: `slots`, `attestations`, `runs`, `trend`, " +
        "`proposers`, `attesters`, `l1gas`. Omit for the summary only.")
        .meta({ example: "slots,attestations,runs" }),
    groupBy: z
        .enum(["auto", "hour", "day", "epoch"])
        .optional()
        .describe("Bucket for `trend` (default `auto`). `auto` sizes the bucket from the selection's own " +
        "span so the series lands in the tens of buckets — a fixed unit cannot: `day` over a " +
        "24h window is one bucket, and `hour` over 30 days is 720. The chosen width is always " +
        "reported back as `trend.bucketSeconds`, and may be a multiple of an hour (3h, 6h, 12h) " +
        "that is not nameable here.")
        .meta({ example: "auto" }),
    limit: z
        .string()
        .optional()
        .describe("Max `slots` rows to return (default 500, cap 5000).")
        .meta({ example: "500" }),
    offset: z
        .string()
        .optional()
        .describe("Offset into the `slots` rows.")
        .meta({ example: "0" }),
});
export const slotStatusRowSchema = z.object({
    slot: z.number(),
    epoch: z.number().nullable().describe("Null when the network has no epoch duration recorded."),
    slotInEpoch: z.number().nullable(),
    status: slotStatusSchema.describe("See the status reference table in the endpoint docs."),
    attestationsSent: z.number().nullable(),
    attestationsMissed: z.number().nullable(),
    committeeSize: z
        .number()
        .nullable()
        .describe("Attesters observed plus the proposer. Null for slots with no attestation activity " +
        "(nothing proposed, so nothing to attest) and for rows recorded before this was captured."),
    proposerAddress: z
        .string()
        .nullable()
        .describe("Elected proposer (sequencer), lowercase hex. Null for rows recorded before proposer " +
        "capture existed — sentinel history is evicted, so those can never be backfilled."),
    slotTimestamp: z
        .number()
        .nullable()
        .describe("Wall-clock start of the slot, unix seconds. Null when genesis time is unknown."),
    blockCount: z
        .number()
        .describe("L2 blocks that landed at this slot. A slot carries a whole checkpoint, not a single " +
        "block, so this is routinely >1 — up to 10 on current testnet data. 0 means nothing " +
        "landed, which independently corroborates a missed verdict."),
    firstBlockNumber: z
        .number()
        .nullable()
        .describe("Lowest L2 block number in the slot, or null when blockCount is 0."),
    lastBlockNumber: z
        .number()
        .nullable()
        .describe("Highest L2 block number in the slot. Equal to firstBlockNumber for a single-block slot."),
    checkpointNumber: z
        .number()
        .nullable()
        .describe("The slot's checkpoint. Single-valued because a slot's blocks all belong to one " +
        "checkpoint — that is what the proposer produced."),
    observedAt: z.string().describe("When the sentinel poll recorded this verdict, ISO-8601."),
});
export const attestationProfileSchema = z.object({
    slots: z.number(),
    sentMin: z.number().nullable(),
    sentAvg: z.number().nullable(),
    sentMax: z.number().nullable(),
    missedAvg: z.number().nullable(),
});
export const proposerReliabilitySchema = z.object({
    address: z.string(),
    slotsAssigned: z
        .number()
        .describe("Slots in the selection this validator was elected to propose, excluding any the rollup " +
        "later discarded — see `totals.orphaned`. A proposer whose checkpoint reached L1 and was " +
        "then pruned away because the epoch went unproven neither succeeded nor failed at its " +
        "job, so such slots are left out of this denominator and out of `missed`."),
    missed: z.number(),
    missedPct: z.number(),
    byStatus: z.record(z.string(), z.number()),
    lastSlot: z.number(),
});
export const attesterReliabilitySchema = z.object({
    address: z.string(),
    seatedSlots: z
        .number()
        .describe("Counted slots in the selection whose epoch seated this validator — the denominator. " +
        "A raw miss count is uninterpretable without it. Excludes slots the rollup later " +
        "discarded (see `totals.orphaned`): the committee attested and the protocol threw the " +
        "result away, which says nothing about the committee."),
    missed: z.number().describe("Counted slots where this validator did not attest."),
    missedPct: z.number(),
    lastMissedSlot: z.number().nullable(),
});
export const l1GasProfileSchema = z.object({
    slotsWithSample: z
        .number()
        .describe("Slots of this status that had an L1 gas sample within the staleness bound. Compare " +
        "against the status's slot count in `statusCounts` before drawing " +
        "conclusions — a low number means thin coverage, not calm fees."),
    baseFeeGweiMin: z.number().nullable(),
    baseFeeGweiMedian: z.number().nullable(),
    baseFeeGweiMax: z.number().nullable(),
    blobBaseFeeGweiMedian: z.number().nullable(),
});
/**
 * Block and checkpoint cadence over the selection — the three time units of Aztec kept
 * distinct. SLOTS have a fixed protocol duration and carry at most one checkpoint;
 * CHECKPOINTS are the unit that lands on L1 and hold one or more L2 blocks; BLOCKS are the
 * L2 chain extension. An interval measured from L1 events is the *checkpoint* cadence
 * (slot duration ÷ occupancy), which overstates block time whenever a checkpoint carries
 * more than one block.
 *
 * This and the slot/attestation sections were two endpoints until they were merged under
 * this one. The cadence half could only window by trailing hours, while the slot half
 * windows by epoch, slot list or explicit range as well — so block cadence per epoch was
 * simply not expressible before. Both also reported slot occupancy, from different tables.
 *
 * Only one of those two occupancy figures survived the merge. This half inferred a miss from
 * the absence of a block row, which cannot tell "the proposer failed" from "we never indexed
 * it"; `totals` and `coverage` answer it from sentinel verdicts and state what they cannot
 * see. So there is no `slots.occupied`/`missedPct` here — that is `totals`.
 */
export const productionProfileSchema = z.object({
    blocks: z.object({
        count: z.number(),
        firstBlock: z.number().nullable(),
        lastBlock: z.number().nullable(),
        avgIntervalSeconds: z
            .number()
            .nullable()
            .describe("Average time between consecutive L2 blocks: timespan ÷ (count − 1). This is the " +
            "figure that reflects L2 block production — NOT the slot duration and NOT the L1 " +
            "checkpoint cadence."),
        p50IntervalSeconds: z.number().nullable().describe("Median gap between consecutive blocks."),
        p95IntervalSeconds: z.number().nullable(),
        emptyPct: z.number().nullable().describe("Share of blocks containing zero transactions, 0–100."),
    }),
    checkpoints: z.object({
        count: z.number(),
        avgIntervalSeconds: z
            .number()
            .nullable()
            .describe("Average time between checkpoints — the unit that lands on L1. Measuring intervals " +
            "on L1 yields this number (≈ slot duration ÷ slot occupancy), which overstates " +
            "block time because each checkpoint carries one or more L2 blocks."),
        avgBlocksPerCheckpoint: z.number().nullable(),
    }),
    /** Wall-clock span of the blocks found, which can be narrower than the requested window. */
    firstBlockTimestamp: z.number().nullable(),
    lastBlockTimestamp: z.number().nullable(),
});
export const slotTrendPointSchema = z.object({
    from: z.number().describe("Bucket start: unix seconds, or epoch number when bucket=epoch."),
    slotsObserved: z.number(),
    missed: z.number(),
    missedPct: z.number(),
    // The outcome mix, not just the rate. A scalar percentage cannot be drawn in the colour of
    // what actually failed: a bucket at 2% missed says nothing about whether those slots were
    // `blocks-missed` (proposer never acted) or `checkpoint-valid` (attested, never reached L1),
    // and a strip that colours by rate band ends up painting an all-`blocks-missed` bucket green.
    statusCounts: z
        .record(z.string(), z.number())
        .describe("Slot count per status within this bucket. Same shape as the top-level `statusCounts`, " +
        "so `deriveMissCauses` applies to a bucket exactly as it does to the whole selection."),
});
export const productionStatsResponseSchema = z
    .object({
    protocol: z.object({
        slotDurationSeconds: z.number().nullable(),
        epochDurationSlots: z.number().nullable(),
        genesisTimestamp: z.number().nullable(),
    }),
    query: z.object({
        fromSlot: z.number(),
        toSlot: z.number(),
        slotsObserved: z
            .number()
            .describe("Slots in the selection that have a recorded status — the aggregate denominator."),
        matched: z.number().describe("Rows matching the selection AND the filters."),
        returned: z.number().describe("Rows actually in `slots` after limit/offset."),
    }),
    // How much of the selection we can actually speak to. Absence of a status row is
    // ambiguous on its own — the sentinel may have evicted the slot before we ever saw it,
    // or our poller may have been down — so the observed sentinel window is reported
    // alongside the gap count rather than leaving callers to assume.
    coverage: z.object({
        slotsInSelection: z
            .number()
            .describe("Slots the selection spans (its span, or the length of the `slots` list)."),
        slotsWithStatus: z.number(),
        slotsMissing: z
            .number()
            .describe("Selection slots with no recorded status at all — see `sentinelWindow`."),
        sentinelOldestSlot: z
            .number()
            .nullable()
            .describe("Oldest slot the node's sentinel reported holding, across recorded polls. Selection " +
            "slots below this were evicted before we could observe them and are permanently " +
            "unknowable. Null when no poll provenance has been recorded yet."),
        sentinelNewestSlot: z
            .number()
            .nullable()
            .describe("Newest slot the sentinel reported processing, across recorded polls."),
    }),
    // Aggregates below always describe the whole selection, never the filtered subset.
    totals: z.object({
        mined: z.number(),
        missed: z.number(),
        indeterminate: z
            .number()
            .describe("Slots with no terminal verdict (checkpoint-unvalidated, unknown). Excluded from " +
            "missedPct's numerator and denominator."),
        orphaned: z
            .number()
            .describe("Slots whose checkpoint reached L1 and was then discarded when the rollup pruned its " +
            "pending chain back to the last proven checkpoint. Neither mined nor missed: the " +
            "proposer did its job and the protocol threw the work away, so these are excluded " +
            "from missedPct's numerator and denominator like `indeterminate` — but for the " +
            "opposite reason, since here we know exactly what happened. Reported under the " +
            "`orphaned` key of `statusCounts` too, which is not a sentinel status but a derived " +
            "bucket."),
        missedPct: z.number(),
    }),
    // Always present: this is the aggregate every other total is a view of, so gating it
    // behind an `include` would force a caller wanting a miss breakdown to ask for the
    // attestation profile as well. `totals` is this folded four ways by slotOutcome, and the
    // miss split is `deriveMissCauses(statusCounts)` from @clustec/common — the endpoint used
    // to ship that array itself, which duplicated counts it was already sending.
    //
    // Keys are sentinel statuses plus one derived bucket, `orphaned`, for slots a rollup prune
    // discarded. It cannot be a sentinel status: the sentinel goes on reporting those slots as
    // `checkpoint-mined` because its history lives in the node's kvStore, which an L2 prune does
    // not rewind.
    statusCounts: z
        .record(z.string(), z.number())
        .describe("Slot count per status over the whole selection, never the filtered subset. Statuses " +
        "absent from the selection are omitted rather than zero-filled."),
    // Core, not include-gated: the endpoint is named for production, and answering "how did
    // this window produce" without cadence would make the headline section optional.
    production: productionProfileSchema,
    slots: z.array(slotStatusRowSchema).optional().describe("Present when include=slots."),
    attestationProfile: z
        .record(z.string(), attestationProfileSchema)
        .optional()
        .describe("Present when include=attestations. Keyed by status and always including " +
        "checkpoint-mined as the baseline — a miss cause whose profile matches the baseline " +
        "was not an attestation failure."),
    runLengths: z
        .record(z.string(), z.record(z.string(), z.number()))
        .optional()
        .describe("Present when include=runs. status → run length → occurrences, over consecutive slots " +
        "sharing a status. Separates isolated misses from correlated bursts."),
    trend: z
        .object({
        // Replaces the old `bucket: "hour" | "day" | "epoch"`. That enum was both too coarse to
        // describe what auto-bucketing picks (3h, 6h, 12h) and the wrong shape for a caller: what
        // a chart needs is how to read `from` and how wide a bucket is, which is exactly these
        // two fields.
        unit: z
            .enum(["time", "epoch"])
            .describe("How to read `from`: unix seconds, or an epoch number."),
        bucketSeconds: z
            .number()
            .nullable()
            .describe("Wall-clock width of one bucket. Null only for epoch buckets on a network whose " +
            "slot duration isn't recorded, where the width in seconds is unknowable."),
        points: z.array(slotTrendPointSchema),
    })
        .optional()
        .describe("Present when include=trend."),
    proposers: z
        .array(proposerReliabilitySchema)
        .optional()
        .describe("Present when include=proposers. Worst-first by missed count. Only covers slots with a " +
        "captured proposer address; see `unattributedSlots`."),
    unattributedSlots: z
        .number()
        .optional()
        .describe("Present when include=proposers. Slots in the selection with no proposer address — " +
        "recorded before proposer capture, hence permanently unattributable."),
    attesters: z
        .object({
        slotsCounted: z
            .number()
            .describe("Slots contributing to the figures below — see the two exclusions."),
        slotsWithoutRoster: z
            .number()
            .describe("Excluded: no committee roster recorded for the slot's epoch, because it predates " +
            "roster capture. Sentinel history is evicted, so these can never be recovered."),
        slotsInconsistent: z
            .number()
            .describe("Excluded: a roster exists but committee size minus recorded misses disagrees with " +
            "attestationsSent, so the sentinel data for that slot was incomplete. Counting " +
            "these would credit validators with attestations that were never observed."),
        validators: z
            .array(attesterReliabilitySchema)
            .describe("Worst-first by miss count. Includes validators with zero misses."),
    })
        .optional()
        .describe("Present when include=attesters. Per-validator attestation reliability, derived from " +
        "the epoch roster (denominator) and recorded misses (numerator)."),
    l1Gas: z
        .object({
        maxSampleAgeSeconds: z
            .number()
            .describe("Staleness bound. A slot is only credited with L1 conditions when a sample exists " +
            "within this many seconds before it; otherwise it is left out rather than " +
            "borrowing a distant sample."),
        byStatus: z.record(z.string(), l1GasProfileSchema),
    })
        .optional()
        .describe("Present when include=l1gas. L1 base fee at the slots of each status, for testing " +
        "whether checkpoint-valid misses (attested but never published) line up with L1 fee " +
        "spikes. Compare each status against checkpoint-mined as the baseline."),
})
    .meta({
    example: {
        protocol: { slotDurationSeconds: 72, epochDurationSlots: 32, genesisTimestamp: 1782803327 },
        query: { fromSlot: 28169, toSlot: 32474, slotsObserved: 4306, matched: 106, returned: 106 },
        totals: { mined: 4200, missed: 106, indeterminate: 0, missedPct: 2.46 },
        statusCounts: { "checkpoint-mined": 4200, "blocks-missed": 72, "checkpoint-valid": 34 },
        production: {
            blocks: {
                count: 7981,
                firstBlock: 7997,
                lastBlock: 15977,
                avgIntervalSeconds: 38.1,
                p50IntervalSeconds: 72,
                p95IntervalSeconds: 72,
                emptyPct: 37.1,
            },
            checkpoints: { count: 4200, avgIntervalSeconds: 72.4, avgBlocksPerCheckpoint: 1.9 },
            firstBlockTimestamp: 1782815168,
            lastBlockTimestamp: 1783119128,
        },
        attestationProfile: {
            "checkpoint-mined": { slots: 4200, sentMin: 44, sentAvg: 46.4, sentMax: 47, missedAvg: 0.58 },
            "checkpoint-valid": { slots: 34, sentMin: 45, sentAvg: 46.4, sentMax: 47, missedAvg: 0.59 },
            "blocks-missed": { slots: 72, sentMin: 0, sentAvg: 0, sentMax: 0, missedAvg: 0 },
        },
        runLengths: { "blocks-missed": { "1": 68, "2": 2 }, "checkpoint-valid": { "2": 17 } },
    },
});
//# sourceMappingURL=production-stats.js.map